import type { ItineraryItemDTO, PlaceDTO } from "@shared/types";

export type LatLng = [number, number];

const rad = (d: number) => (d * Math.PI) / 180;
const deg = (r: number) => (r * 180) / Math.PI;

/** Дуга большого круга (для перелётов) */
export function greatCircle(a: LatLng, b: LatLng, n = 64): LatLng[] {
  const [la1, lo1] = [rad(a[0]), rad(a[1])];
  const [la2, lo2] = [rad(b[0]), rad(b[1])];
  const d = 2 * Math.asin(Math.sqrt(Math.sin((la2 - la1) / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin((lo2 - lo1) / 2) ** 2));
  if (d < 1e-6) return [a, b];
  const out: LatLng[] = [];
  for (let i = 0; i <= n; i++) {
    const f = i / n;
    const A = Math.sin((1 - f) * d) / Math.sin(d);
    const B = Math.sin(f * d) / Math.sin(d);
    const x = A * Math.cos(la1) * Math.cos(lo1) + B * Math.cos(la2) * Math.cos(lo2);
    const y = A * Math.cos(la1) * Math.sin(lo1) + B * Math.cos(la2) * Math.sin(lo2);
    const z = A * Math.sin(la1) + B * Math.sin(la2);
    out.push([deg(Math.atan2(z, Math.sqrt(x * x + y * y))), deg(Math.atan2(y, x))]);
  }
  return out;
}

/** Слегка изогнутая линия для коротких перелётов (красивее, чем прямая) */
export function bentLine(a: LatLng, b: LatLng, bend = 0.18, n = 24): LatLng[] {
  const mid: LatLng = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  const dx = b[1] - a[1];
  const dy = b[0] - a[0];
  const ctrl: LatLng = [mid[0] + dx * bend, mid[1] - dy * bend];
  const out: LatLng[] = [];
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    out.push([
      (1 - t) ** 2 * a[0] + 2 * (1 - t) * t * ctrl[0] + t ** 2 * b[0],
      (1 - t) ** 2 * a[1] + 2 * (1 - t) * t * ctrl[1] + t ** 2 * b[1],
    ]);
  }
  return out;
}

export function distanceKm(a: LatLng, b: LatLng) {
  const R = 6371;
  const dLat = rad(b[0] - a[0]);
  const dLng = rad(b[1] - a[1]);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a[0])) * Math.cos(rad(b[0])) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

export function boundsOf(pts: LatLng[]): [LatLng, LatLng] | null {
  if (!pts.length) return null;
  const lats = pts.map((p) => p[0]);
  const lngs = pts.map((p) => p[1]);
  return [
    [Math.min(...lats), Math.min(...lngs)],
    [Math.max(...lats), Math.max(...lngs)],
  ];
}

/** Точки пункта плана: посещение — одна точка, переезд — две */
export function itemPoints(it: ItineraryItemDTO, places: Map<string, PlaceDTO>): LatLng[] {
  if (it.kind === "transport") {
    const pts: LatLng[] = [];
    if (it.fromLat != null && it.fromLng != null) pts.push([it.fromLat, it.fromLng]);
    if (it.toLat != null && it.toLng != null) pts.push([it.toLat, it.toLng]);
    return pts;
  }
  const p = it.placeId ? places.get(it.placeId) : null;
  return p ? [[p.lat, p.lng]] : [];
}

export const gmapsUrl = (lat: number, lng: number, name?: string) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(name ? `${name}` : `${lat},${lng}`)}&query_place_id=&center=${lat},${lng}`;
export const gmapsPointUrl = (lat: number, lng: number) => `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
export const gmapsDirUrl = (lat: number, lng: number) => `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
export const appleMapsUrl = (lat: number, lng: number, name?: string) => `https://maps.apple.com/?ll=${lat},${lng}&q=${encodeURIComponent(name ?? "")}`;
